/**
 * LivePagePreview — mini-makieta wygenerowanej strony, reagująca na wybór fontu.
 * Pokazuje na żywo, jak nagłówek/tekst/przycisk wyglądają w wariancie Elegant vs Modern,
 * używając realnych danych z kreatora (nazwa, kolor akcentu, pierwsze zdjęcie).
 */
import { Phone } from 'lucide-react';

const FONTS = {
  elegant: { head: "'Cormorant Garamond', Georgia, 'Times New Roman', serif", body: "'Manrope', system-ui, sans-serif" },
  modern: { head: "'Space Grotesk', system-ui, sans-serif", body: "'Inter', system-ui, sans-serif" },
};

const ACCENTS = {
  gold: '#c9a96e', blue: '#6e8ec9', green: '#6ec98a', red: '#c96e6e', purple: '#8e6ec9', teal: '#6ec9b8',
  'pastel-pink': '#f4b8c1', 'pastel-blue': '#a8c8e8', 'pastel-mint': '#a8dbc5',
  'pastel-lavender': '#c5b8d9', 'pastel-peach': '#f4c9a8', 'pastel-yellow': '#f0e0a0',
};

// Motywy tła — spójne z PublicKennel (dark/light/green/blue) i PublicBusiness (dark/light).
const THEME_BG = {
  dark: { bg: '#0A0E1A', rgb: '10,14,26', text: '#ffffff', muted: 'rgba(255,255,255,0.72)', accent: '#fbbf24', light: false },
  light: { bg: '#FDFBF7', rgb: '253,251,247', text: '#111827', muted: '#6b7280', accent: '#b45309', light: true },
  green: { bg: '#0A1F13', rgb: '10,31,19', text: '#ffffff', muted: 'rgba(209,250,229,0.7)', accent: '#34d399', light: false },
  blue: { bg: '#0B1221', rgb: '11,18,33', text: '#ffffff', muted: 'rgba(224,242,254,0.7)', accent: '#38bdf8', light: false },
};

export default function LivePagePreview({
  fontStyle = 'elegant', eyebrow = '', title = '', subtitle = '', image = '',
  accentKey = 'gold', accentHex, theme = 'dark', ctaLabel = 'Kontakt',
}) {
  const fonts = FONTS[fontStyle] || FONTS.elegant;
  const t = THEME_BG[theme] || THEME_BG.dark;
  const accent = accentHex || (accentKey && ACCENTS[accentKey]) || t.accent;
  const isLight = t.light;
  const bg = t.bg;
  const textMain = t.text;
  const textMuted = t.muted;
  const overlay = isLight
    ? `linear-gradient(to top, rgba(${t.rgb},0.92), rgba(${t.rgb},0.55))`
    : `linear-gradient(to top, rgba(${t.rgb},0.92), rgba(${t.rgb},0.42))`;

  return (
    <div data-testid="live-page-preview" className="rounded-2xl border border-gray-200 bg-white p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-sm font-semibold text-gray-900">Podgląd Twojej strony</p>
        <span className="text-[11px] font-medium text-amber-600">na żywo</span>
      </div>
      <div
        data-testid="live-page-preview-canvas"
        className="relative overflow-hidden rounded-xl border border-gray-100"
        style={{ aspectRatio: '16 / 10', background: bg }}
      >
        {image ? (
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `radial-gradient(120% 120% at 50% 0%, ${accent}33, transparent 60%), ${bg}` }}
          />
        )}
        <div className="absolute inset-0" style={{ background: overlay }} />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4 py-3">
          <div key={fontStyle} className="wiz-preview-fade flex flex-col items-center w-full">
            {eyebrow && (
              <span
                style={{ fontFamily: fonts.body, color: accent, letterSpacing: '0.22em' }}
                className="text-[9px] sm:text-[10px] font-semibold uppercase mb-1.5 truncate max-w-full"
              >
                {eyebrow}
              </span>
            )}
            <span
              style={{ fontFamily: fonts.head, color: textMain, lineHeight: 1.05 }}
              className="text-2xl sm:text-3xl md:text-4xl font-semibold max-w-[92%] break-words"
            >
              {title || 'Twoja strona'}
            </span>
            {subtitle && (
              <span
                style={{ fontFamily: fonts.body, color: textMuted }}
                className="mt-1.5 text-[11px] sm:text-xs max-w-[85%] truncate"
              >
                {subtitle}
              </span>
            )}
            <span
              style={{ backgroundColor: accent, color: isLight ? '#1a1a1a' : '#0b0f1a', fontFamily: fonts.body }}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold shadow-sm"
            >
              <Phone className="w-3 h-3" /> {ctaLabel}
            </span>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-2 px-1">Tak Twoją stronę zobaczą klienci.</p>
    </div>
  );
}
